/*!
 * Doctor Print — storefront configurator
 * Renders inside the "Doctor Print Configurator" section on configurable product pages.
 * Reads handle -> family from window.DP_CONFIG, fetches material list + live price from
 * the intake service, drag-drops artwork uploads, and finally calls /api/draft-order to
 * create a Shopify draft order (price-enforced server-side) -> redirects to invoice URL.
 */
(function () {
  'use strict';

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var CFG = window.DP_CONFIG || {};
  var INTAKE = (CFG.intakeBase || '').replace(/\/$/, '');

  // Handle -> family map. Keys must match Shopify product handles.
  var HANDLE_TO_FAMILY = {
    'custom-die-cut-stickers': { family: 'die-cut-sticker', route: 'latex+cutter', defaults: { widthMm: 75, heightMm: 75, qty: 100, material: 'gloss-vinyl' } },
    'custom-kiss-cut-sticker-sheets': { family: 'kiss-cut-sheet', route: 'latex+cutter', defaults: { widthMm: 100, heightMm: 150, qty: 50, material: 'gloss-vinyl' } },
    'custom-roll-labels': { family: 'roll-label', route: 'latex+cutter', defaults: { widthMm: 60, heightMm: 40, qty: 500, material: 'white-bopp' } },
    'rigid-signs': { family: 'rigid-sign', route: 'latex+flatbed', defaults: { widthMm: 600, heightMm: 900, qty: 1, material: 'corflute-5mm' } },
    'window-floor-graphics': { family: 'window-floor', route: 'latex', defaults: { widthMm: 1000, heightMm: 1000, qty: 1, material: 'gloss-vinyl' } },
    'vehicle-decals': { family: 'vehicle-decal', route: 'latex+cutter', defaults: { widthMm: 300, heightMm: 300, qty: 2, material: 'cast-vinyl' } }
  };

  // Pretty labels for materials (technical -> customer-friendly)
  var MAT_LABELS = {
    'gloss-vinyl': 'Gloss Vinyl', 'matte-vinyl': 'Matte Vinyl', 'clear-vinyl': 'Clear Vinyl',
    'white-vinyl': 'White Vinyl', 'holographic': 'Holographic',
    'paper': 'Paper',
    'gloss-paper': 'Gloss Paper', 'matte-paper': 'Matte Paper',
    'clear-bopp': 'Clear BOPP (premium)', 'white-bopp': 'White BOPP (premium)',
    'corflute-5mm': 'Corflute 5mm', 'acm-3mm': 'ACM (aluminium) 3mm', 'foamboard': 'Foamboard',
    'frosted': 'Frosted Vinyl', 'one-way': 'One-Way Vision', 'anti-slip-floor': 'Anti-Slip Floor',
    'cast-vinyl': 'Cast Vinyl', 'reflective': 'Reflective Vinyl',
    'vinyl-banner': 'Vinyl Banner', 'mesh': 'Mesh Banner'
  };

  function init(root) {
    var handle = root.getAttribute('data-product-handle');
    var def = HANDLE_TO_FAMILY[handle];
    if (!def) { root.style.display = 'none'; return; }

    // Buy-buttons are suppressed server-side by the Liquid wrap in blocks/buy-buttons.liquid
    // (installed via scripts/install-buybutton-wrap.js). No client-side hide here — devtools
    // could re-show or click before the setTimeout fired. Configurator is the only purchase path.

    var productTitle = root.getAttribute('data-product-title') || '';
    var productSku = root.getAttribute('data-product-sku') || '';

    var state = {
      family: def.family, route: def.route, productHandle: handle,
      productTitle: productTitle, sku: productSku,
      widthMm: def.defaults.widthMm, heightMm: def.defaults.heightMm,
      qty: def.defaults.qty, material: def.defaults.material,
      laminate: false, artworkUrl: null, artworkFilename: null
    };

    // Pull families catalogue (materials list) once per page load
    fetch(INTAKE + '/api/families').then(function (r) { return r.json(); }).then(function (j) {
      if (!j || !j.ok) throw new Error('families catalogue unavailable');
      render(root, state, j.families[def.family]);
    }).catch(function (e) {
      root.innerHTML = '<div class="dp-error">Configurator offline: ' + e.message + '</div>';
    });
  }

  function render(root, state, famMeta) {
    var matOpts = famMeta.materials.map(function (m) {
      var sel = m === state.material ? ' selected' : '';
      var lbl = MAT_LABELS[m] || m;
      return '<option value="' + m + '"' + sel + '>' + lbl + '</option>';
    }).join('');

    var areaHint = famMeta.areaUnit === 'm2' ? 'Sizes up to 1000 × 1500 mm' : 'Common sticker sizes 25 × 25 mm to 300 × 300 mm';
    var laminateRow = famMeta.laminate
      ? '<label class="dp-row dp-row-check"><input type="checkbox" id="dp-laminate"' + (state.laminate ? ' checked' : '') + '> Add weather-resistant laminate</label>'
      : '';

    root.innerHTML = [
      '<div class="dp-card">',
      '  <h3 class="dp-title">Configure your order</h3>',
      '  <div class="dp-grid">',
      '    <label class="dp-field"><span>Width (mm)</span><input id="dp-w" type="number" min="10" max="3000" step="1" value="' + state.widthMm + '"></label>',
      '    <label class="dp-field"><span>Height (mm)</span><input id="dp-h" type="number" min="10" max="3000" step="1" value="' + state.heightMm + '"></label>',
      '    <label class="dp-field"><span>Material</span><select id="dp-mat">' + matOpts + '</select></label>',
      '    <label class="dp-field"><span>Quantity</span><input id="dp-q" type="number" min="1" max="100000" step="1" value="' + state.qty + '"></label>',
      '  </div>',
      '  <div class="dp-hint">' + areaHint + '. Min order $' + famMeta.orderMin + '.</div>',
      '  ' + laminateRow,
      '  <div class="dp-upload" id="dp-upload">',
      '    <div class="dp-upload-inner">',
      '      <strong>Drop your artwork here</strong>',
      '      <div class="dp-upload-sub">PDF, AI, EPS, SVG, PNG, JPG, TIFF · up to 100 MB</div>',
      '      <button type="button" class="dp-btn-secondary" id="dp-browse">Choose file</button>',
      '      <input type="file" id="dp-file" accept=".pdf,.ai,.eps,.svg,.png,.jpg,.jpeg,.tif,.tiff" hidden>',
      '      <div class="dp-upload-status" id="dp-upload-status"></div>',
      '    </div>',
      '  </div>',
      '  <div class="dp-price-row">',
      '    <div>',
      '      <div class="dp-price-label">Total</div>',
      '      <div class="dp-price" id="dp-total">—</div>',
      '      <div class="dp-unit" id="dp-unit"></div>',
      '    </div>',
      '    <button type="button" class="dp-btn-primary" id="dp-order">Configure & Order</button>',
      '  </div>',
      '  <div class="dp-msg" id="dp-msg"></div>',
      '</div>'
    ].join('\n');

    var els = {
      w: $('#dp-w', root), h: $('#dp-h', root), mat: $('#dp-mat', root), q: $('#dp-q', root),
      lam: $('#dp-laminate', root), upload: $('#dp-upload', root), browse: $('#dp-browse', root),
      file: $('#dp-file', root), uploadStatus: $('#dp-upload-status', root),
      total: $('#dp-total', root), unit: $('#dp-unit', root), order: $('#dp-order', root), msg: $('#dp-msg', root)
    };

    function syncState() {
      state.widthMm = Number(els.w.value) || 0;
      state.heightMm = Number(els.h.value) || 0;
      state.qty = Math.max(1, Math.floor(Number(els.q.value) || 1));
      state.material = els.mat.value;
      state.laminate = !!(els.lam && els.lam.checked);
    }

    var priceTimer = null;
    function refreshPrice() {
      syncState();
      clearTimeout(priceTimer);
      priceTimer = setTimeout(function () {
        fetch(INTAKE + '/api/price', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            family: state.family, widthMm: state.widthMm, heightMm: state.heightMm,
            qty: state.qty, material: state.material, laminate: state.laminate
          })
        }).then(function (r) { return r.json(); }).then(function (j) {
          if (!j.ok) { els.total.textContent = '—'; els.unit.textContent = j.error || ''; return; }
          els.total.textContent = '$' + Number(j.total).toFixed(2);
          els.unit.textContent = '$' + Number(j.unitPrice).toFixed(2) + ' each';
        }).catch(function () {
          els.total.textContent = '—';
          els.unit.textContent = 'price service offline';
        });
      }, 220);
    }

    ['input', 'change'].forEach(function (ev) {
      [els.w, els.h, els.mat, els.q].forEach(function (el) { el.addEventListener(ev, refreshPrice); });
    });
    if (els.lam) els.lam.addEventListener('change', refreshPrice);
    refreshPrice();

    // ---- drag-drop + browse upload ----
    function setUploadStatus(html, kind) {
      els.uploadStatus.innerHTML = html;
      els.uploadStatus.className = 'dp-upload-status' + (kind ? ' dp-upload-status-' + kind : '');
    }

    function uploadFile(file) {
      if (!file) return;
      if (file.size > 100 * 1024 * 1024) { setUploadStatus('File too large (max 100 MB).', 'err'); return; }
      setUploadStatus('Uploading ' + escapeHtml(file.name) + '…');
      var fd = new FormData(); fd.append('artwork', file);
      fetch(INTAKE + '/api/upload', { method: 'POST', body: fd })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (!j.ok) throw new Error(j.error || 'upload failed');
          state.artworkUrl = j.url; state.artworkFilename = file.name;
          setUploadStatus('✓ ' + escapeHtml(file.name) + ' (' + Math.round(file.size / 1024) + ' KB) uploaded — ready to order', 'ok');
        })
        .catch(function (e) { setUploadStatus('Upload failed: ' + e.message, 'err'); });
    }

    els.browse.addEventListener('click', function () { els.file.click(); });
    els.file.addEventListener('change', function () { uploadFile(els.file.files[0]); });

    ['dragenter', 'dragover'].forEach(function (ev) {
      els.upload.addEventListener(ev, function (e) { e.preventDefault(); e.stopPropagation(); els.upload.classList.add('dp-upload-hover'); });
    });
    ['dragleave', 'drop'].forEach(function (ev) {
      els.upload.addEventListener(ev, function (e) { e.preventDefault(); e.stopPropagation(); els.upload.classList.remove('dp-upload-hover'); });
    });
    els.upload.addEventListener('drop', function (e) {
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) uploadFile(f);
    });

    // ---- order: create draft order, redirect to invoice ----
    els.order.addEventListener('click', function () {
      syncState();
      els.msg.textContent = '';
      if (!state.artworkUrl) {
        els.msg.innerHTML = '<span class="dp-msg-warn">Please upload your artwork first — it\'s required for production.</span>';
        return;
      }
      els.order.disabled = true; els.order.textContent = 'Creating order…';
      fetch(INTAKE + '/api/draft-order', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          family: state.family, widthMm: state.widthMm, heightMm: state.heightMm,
          qty: state.qty, material: state.material, laminate: state.laminate,
          artworkUrl: state.artworkUrl,
          productHandle: state.productHandle, productTitle: state.productTitle,
          sku: state.sku, routeTag: state.route
        })
      }).then(function (r) { return r.json(); }).then(function (j) {
        if (!j.ok || !j.invoiceUrl) throw new Error(j.error || 'could not create order');
        els.msg.innerHTML = '<span class="dp-msg-ok">Order ' + j.draftOrderName + ' created — redirecting to checkout…</span>';
        window.location.href = j.invoiceUrl;
      }).catch(function (e) {
        els.order.disabled = false; els.order.textContent = 'Configure & Order';
        els.msg.innerHTML = '<span class="dp-msg-err">' + escapeHtml(e.message) + '</span>';
      });
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function boot() {
    $$('[data-dp-configurator]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
